import type { MeQuery } from 'core/graphql';
import { UserRole } from 'context';

export interface IAmountDue {
  amount: number;
  currency: string;
}

export interface ILmsAccess {
  userId: string | null;
  activeRole: string | null;
  isStudent: boolean;
  /** Приостановка по `me` — только у активной роли Student. */
  isBlocked: boolean;
  blockedAt: string | null;
  /** Суммы к оплате по валютам — никогда не складываются между собой. */
  amountsDue: IAmountDue[];
  unpaidInvoicesCount: number;
  overdueInvoicesCount: number;
}

/**
 * `me` → то, что нужно LMS для счётчика, поп-апа и приостановки (ТЗ 0074).
 *
 * Роль — ЖИВАЯ, из authState (`liveRole`), а `me.activeRole` только запасной
 * вариант, пока authState роль ещё не знает (старт до refresh). Обратный
 * порядок давал ложный редирект: при смене роли Student → Teacher authState
 * меняется синхронно, а `me` в кеше до `resetStore()` ещё студенческий и
 * заблокированный — гард уводил преподавателя на /invoices.
 *
 * Всё, что связано с фактурами, у не-студента обнуляется: человек, который
 * сейчас работает преподавателем, не должен видеть «у вас долг» за свою
 * студенческую роль.
 */
export const transformLmsAccess = (
  me: MeQuery['me'] | undefined,
  liveRole: string | null = null,
): ILmsAccess => {
  const activeRole = liveRole || me?.activeRole || null;
  const isStudent = activeRole === UserRole.STUDENT;
  const block = me?.lmsAccessBlock;

  return {
    userId: me?.id ?? null,
    activeRole,
    isStudent,
    isBlocked: isStudent && Boolean(block?.isBlocked),
    blockedAt: isStudent ? block?.blockedAt ?? null : null,
    amountsDue: isStudent
      ? (block?.amountsDue ?? [])
          .filter(Boolean)
          .map(({ amount, currency }) => ({ amount, currency }))
      : [],
    unpaidInvoicesCount: isStudent ? me?.unpaidInvoicesCount ?? 0 : 0,
    overdueInvoicesCount: isStudent ? me?.overdueInvoicesCount ?? 0 : 0,
  };
};
